import { useMemo } from 'react'
import { DataSource } from '@/features/capacity-planner/types'
import { useCapacitySettings } from '@/features/capacity-planner/hooks'
import { usePlatformConnections } from '@/features/platform/hooks'
import { useAuthSession } from '@/lib/session'
import { PlatformType } from '@/types/authorization'

function hasLegacyStaffDataPlatform(platformTypes: number[] | undefined) {
  return Boolean(
    platformTypes?.includes(PlatformType.BRWFM) ||
    platformTypes?.includes(PlatformType.FYI)
  )
}

function isXpmCapacityDataSource(dataSource: DataSource | undefined) {
  return dataSource === DataSource.XpmJob || dataSource === DataSource.XpmJobTask
}

export function useStaffDataEligibility(clientId: string) {
  const session = useAuthSession()
  const hasLegacyAccess = hasLegacyStaffDataPlatform(session?.platformType)
  const shouldCheckXpmAccess = Boolean(clientId) && !hasLegacyAccess

  const platformConnectionsQuery = usePlatformConnections(
    { clientId },
    { enabled: shouldCheckXpmAccess }
  )
  const capacitySettingsQuery = useCapacitySettings(clientId, { enabled: shouldCheckXpmAccess })

  const hasXpmAccess = useMemo(() => {
    if (!shouldCheckXpmAccess) return false

    const hasXpmPlatform = platformConnectionsQuery.data?.some((platform) => platform.type === 'XPM') ?? false
    return hasXpmPlatform && isXpmCapacityDataSource(capacitySettingsQuery.data?.dataSource)
  }, [capacitySettingsQuery.data?.dataSource, platformConnectionsQuery.data, shouldCheckXpmAccess])

  const isLoading = shouldCheckXpmAccess && (platformConnectionsQuery.isLoading || capacitySettingsQuery.isLoading)

  return {
    isEligible: hasLegacyAccess || hasXpmAccess,
    isLoading,
    hasLegacyAccess,
    hasXpmAccess,
  }
}
